import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Share, PlusSquare, MoreVertical, Download, Copy, MessageCircle, Smartphone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
export const isMobile = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent);

let deferredPrompt = null;
if (typeof window !== "undefined") window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; window.dispatchEvent(new Event("vessel-installable")); });

const APP_URL = window.location.origin;
const SHARE_TEXT = `Alora brother! Install the IOTA PRIVATE VESSEL app on your phone: open ${APP_URL} and tap "Add to Home Screen". Sign in with your member account.`;

function Step({ n, icon: Icon, children }) {
  return <li className="flex items-start gap-3 text-sm"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--gold))] text-xs font-700 text-[#0A192F]">{n}</span><span className="flex-1 leading-relaxed">{children}</span>{Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--gold))]" />}</li>;
}

// "Install the app" guide — the vessel is a PWA: no App Store needed, link-only, works like a native app
export default function InstallApp({ open, onClose }) {
  const [canPrompt, setCanPrompt] = useState(!!deferredPrompt);
  useEffect(() => { const h = () => setCanPrompt(true); window.addEventListener("vessel-installable", h); return () => window.removeEventListener("vessel-installable", h); }, []);
  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null; setCanPrompt(false);
    if (outcome === "accepted") { toast.success("The vessel is on your home screen ⚓"); onClose(); }
  };
  const copy = () => { navigator.clipboard.writeText(APP_URL); toast.success("App link copied"); };
  const ios = isIOS(), installed = isStandalone();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-md" data-testid="install-app-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><Smartphone className="h-5 w-5 text-[hsl(var(--gold))]" />Install the Vessel on your phone</DialogTitle></DialogHeader>
        {installed ? (
          <p className="flex items-center gap-2 text-sm" data-testid="install-app-installed"><CheckCircle2 className="h-5 w-5 text-emerald-500" />You are already sailing from the installed app.</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">No App Store needed — the vessel installs straight from this private link and opens full-screen with the Galleon crest, like any other app.</p>
            {canPrompt && <Button onClick={install} className="w-full bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="install-app-button"><Download className="mr-2 h-4 w-4" />Install now</Button>}
            {ios ? (
              <ol className="space-y-2" data-testid="install-steps-ios">
                <Step n={1} icon={Share}>Open this page in <b>Safari</b> and tap the <b>Share</b> button (the square with an arrow, bottom of the screen).</Step>
                <Step n={2} icon={PlusSquare}>Scroll down and tap <b>Add to Home Screen</b>.</Step>
                <Step n={3}>Tap <b>Add</b> — the IOTA Vessel icon appears on your home screen.</Step>
              </ol>
            ) : (
              <ol className="space-y-2" data-testid="install-steps-android">
                <Step n={1} icon={MoreVertical}>Open this page in <b>Chrome</b> and tap the <b>⋮ menu</b> (top right).</Step>
                <Step n={2} icon={Download}>Tap <b>Install app</b> (or <b>Add to Home screen</b>).</Step>
                <Step n={3}>Confirm — the IOTA Vessel icon appears on your home screen.</Step>
              </ol>
            )}
          </>
        )}
        <div className="rounded-lg border border-[hsl(var(--gold))]/25 bg-secondary/40 p-3">
          <p className="text-xs font-700">Share the app with a brother</p>
          <p className="mt-0.5 break-all font-mono-data text-[0.65rem] text-muted-foreground" data-testid="install-app-link">{APP_URL}</p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" className="gold-ring" onClick={copy} data-testid="install-app-copy"><Copy className="mr-1.5 h-3.5 w-3.5" />Copy link</Button>
            <a href={`https://wa.me/?text=${encodeURIComponent(SHARE_TEXT)}`} target="_blank" rel="noopener noreferrer"><Button size="sm" className="bg-[#25D366] font-700 text-white hover:bg-[#1da851]" data-testid="install-app-whatsapp"><MessageCircle className="mr-1.5 h-3.5 w-3.5" />WhatsApp</Button></a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Slim banner for brothers browsing on a phone who have not installed the app yet
export function InstallBanner({ onOpen }) {
  const [hidden, setHidden] = useState(() => localStorage.getItem("installBannerDismissed") === "1");
  if (hidden || isStandalone() || !isMobile()) return null;
  return (
    <div className="mb-6 flex items-center gap-3 rounded-xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/10 p-3" data-testid="install-banner">
      <img src="/icon-192.png" alt="" className="h-10 w-10 rounded-xl shadow" />
      <div className="min-w-0 flex-1"><p className="text-sm font-700">Get the Vessel app</p><p className="text-xs text-muted-foreground">Add it to your home screen — one tap, no App Store.</p></div>
      <Button size="sm" className="bg-[hsl(var(--gold))] font-700 text-[#0A192F]" onClick={onOpen} data-testid="install-banner-button">Install</Button>
      <button onClick={() => { localStorage.setItem("installBannerDismissed", "1"); setHidden(true); }} className="text-xs text-muted-foreground" aria-label="Dismiss" data-testid="install-banner-dismiss">✕</button>
    </div>
  );
}
